// components/POS.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { 
  PlusIcon, MinusIcon, XCircleIcon, 
  CurrencyDollarIcon,
  CreditCardIcon, 
  MagnifyingGlassIcon, UserIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';

import productAPI from '../../api/productAPI';
import customerAPI from '../../api/customerAPI';
import invoiceAPI from '../../api/invoiceAPI'; 
import BarcodeScanner from '../../components/BarcodeScanner';
import orderAPI from '../../api/orderAPI';
import { formatNumber } from '../../utils/numberUtils';

const initialOrderState = {
  customer: { id: '', name: '', phone: '', accountNumber: '', taxExempt: false },
  salesman: '',
  payment: { method: 'cash', tendered: 0, changeDue: 0 },
  projectName: '',
  willCall: false,
  deliveryRequired: false,
  items: []
};

const SalesOrderPage = () => {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({ type: null, message: null });
  const [order, setOrder] = useState(initialOrderState);
  const [scanStatus, setScanStatus] = useState({ loading: false, error: null });
  const productGridRef = useRef(null);
  const [scanningActive, setScanningActive] = useState(true);
  const [printStatus, setPrintStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    accountNumber: '',
    taxExempt: false
  });

  // Memoized filtered products
  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(term) ||
      (product.sku && product.sku.toLowerCase().includes(term))
    );
  }, [products, searchTerm]);

  // Calculate order totals
  const { subtotal, tax, discountTotal, total } = useMemo(() => {
    const subtotal = order.items.reduce((sum, item) => 
      sum + (item.priceAtSale * item.quantity), 0);
    const discountTotal = order.items.reduce((sum, item) => 
      sum + (item.discount * item.quantity), 0);
    const tax = 0;
    const total = subtotal - discountTotal;

    return { subtotal, tax, discountTotal, total };
  }, [order.items]);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      if (!isAuthenticated || !getAccessToken()) return;

      try {
        const productsData = await productAPI.getAll();
        console.log('productsData', productsData);
        setProducts(productsData.data);
      } catch (err) {
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [isAuthenticated]);

  // Real customer search using your API
const searchCustomers = useCallback(async (query) => {
  if (!query.trim()) {
    setCustomerSuggestions([]);
    setShowSuggestions(false);
    return;
  }

  try {
    setSearchingCustomers(true);
    console.log('Searching for customers with query:', query);
    
    // Try alternative approach - get all and filter locally
    console.log('Using fallback: getting all customers and filtering locally');
    const response = await customerAPI.getAll();
    
    // Extract customers from response
    let allCustomers = [];
    
    if (Array.isArray(response)) {
      allCustomers = response;
    } else if (response && Array.isArray(response.data)) {
      allCustomers = response.data;
    } else if (response && response.success && Array.isArray(response.data)) {
      allCustomers = response.data;
    } else {
      console.warn('Unexpected response format:', response);
      allCustomers = [];
    }
    
    console.log('Total customers from getAll:', allCustomers.length);
    
    // Filter customers locally
    const searchTerm = query.toLowerCase();
    const filteredCustomers = allCustomers.filter(customer => {
      if (!customer) return false;
      
      return (
        (customer.name && customer.name.toLowerCase().includes(searchTerm)) ||
        (customer.accountNumber && customer.accountNumber.toLowerCase().includes(searchTerm)) ||
        (customer.phone && customer.phone.includes(query)) || // phone might have formatting
        (customer.email && customer.email.toLowerCase().includes(searchTerm))
      );
    });
    
    console.log('Filtered customers:', filteredCustomers.length);
    console.log('Filtered results:', filteredCustomers);
    
    setCustomerSuggestions(filteredCustomers);
    setShowSuggestions(true);
    
  } catch (error) {
    console.error('Customer search failed:', error);
    
    // More specific error handling
    if (error.response) {
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
    }
    
    setCustomerSuggestions([]);
    setError({ 
      type: 'error', 
      message: 'Unable to search customers at the moment' 
    });
  } finally {
    setSearchingCustomers(false);
  }
}, []);

  // Debounced customer search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCustomers(customerQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerQuery, searchCustomers]);

  // Create new customer in database
  const createNewCustomer = useCallback(async (customerData) => {
    try {
      setLoading(true);
      const response = await customerAPI.create(customerData);
      
      // Refresh customer suggestions to include the new customer
      if (customerData.name) {
        searchCustomers(customerData.name);
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Failed to create customer:', error);
      throw new Error(error.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  }, [searchCustomers]);

  // Handle create new customer with form
  const handleOpenCustomerForm = useCallback(() => {
    setNewCustomerData({
      name: customerQuery,
      phone: '',
      email: '',
      accountNumber: '',
      taxExempt: false
    });
    setShowCustomerForm(true);
    setShowSuggestions(false);
  }, [customerQuery]);

  // Handle submit new customer form
  const handleSubmitNewCustomer = useCallback(async () => {
    if (!newCustomerData.name.trim()) {
      setError({ type: 'error', message: 'Customer name is required' });
      return;
    }

    try {
      setLoading(true);
      const createdCustomer = await createNewCustomer(newCustomerData);
      
      // Set the new customer in the order WITH THE ID
      setOrder(prev => ({
        ...prev,
        customer: {
          id: createdCustomer._id || createdCustomer.id, // Store the ID
          name: createdCustomer.name,
          phone: createdCustomer.phone || '',
          accountNumber: createdCustomer.accountNumber || '',
          taxExempt: createdCustomer.taxExempt || false
        }
      }));
      
      setCustomerQuery(createdCustomer.name);
      setShowCustomerForm(false);
      setNewCustomerData({
        name: '',
        phone: '',
        email: '',
        accountNumber: '',
        taxExempt: false
      });
      
      setError({ type: 'success', message: 'Customer created successfully!' });
      
    } catch (error) {
      setError({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  }, [newCustomerData, createNewCustomer]);

  // Quick create customer (minimal info)
  const handleQuickCreateCustomer = useCallback(async () => {
    const customerName = customerQuery.trim();
    if (!customerName) {
      setError({ type: 'error', message: 'Please enter a customer name' });
      return;
    }

    try {
      setLoading(true);
      const customerData = {
        name: customerName,
        phone: '',
        email: '',
        accountNumber: '',
        taxExempt: false
      };

      const createdCustomer = await createNewCustomer(customerData);
      
      // Set the new customer in the order WITH THE ID
      setOrder(prev => ({
        ...prev,
        customer: {
          id: createdCustomer._id || createdCustomer.id, // Store the ID
          name: createdCustomer.name,
          phone: createdCustomer.phone || '',
          accountNumber: createdCustomer.accountNumber || '',
          taxExempt: createdCustomer.taxExempt || false
        }
      }));
      
      setCustomerQuery(createdCustomer.name);
      setShowSuggestions(false);
      
      setError({ type: 'success', message: 'Customer created successfully!' });
      
    } catch (error) {
      setError({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  }, [customerQuery, createNewCustomer]);

  // Add product to order
  const addProduct = useCallback((product) => {
    setOrder(prev => {
      const existingIndex = prev.items.findIndex(item => item.product === product._id);
      
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + 1
        };
        return { ...prev, items: newItems };
      }
      
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            product: product._id,
            quantity: 1,
            priceAtSale: product.price,
            discount: 0,
            taxExempt: prev.customer.taxExempt,
            productDetails: product
          }
        ]
      };
    });
  }, []);

  // Enhanced barcode handler
  const handleBarcodeScan = useCallback(async (barcode) => {
    try {
      setScanStatus({ loading: true, error: null });
      
      const product = await productAPI.getByBarcode(barcode);
      
      if (product) {
        addProduct(product);
        highlightProduct(product._id);
      } else {
        setScanStatus({ loading: false, error: 'Product not found' });
      }
    } catch (error) {
      setScanStatus({ loading: false, error: error.message || 'Scan failed' });
    }
  }, [addProduct]);

  // Highlight scanned product in grid
  const highlightProduct = useCallback((productId) => {
    if (productGridRef.current) {
      const productElement = productGridRef.current.querySelector(
        `#product-${productId}`
      );
      
      if (productElement) {
        productElement.classList.add('ring-2', 'ring-green-500');
        setTimeout(() => {
          productElement.classList.remove('ring-2', 'ring-green-500');
        }, 1000);
        
        // Scroll to product if not visible
        productElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, []);

  // Update item quantity with decimal support
  const updateQuantity = useCallback((productId, newQuantity) => {
    setOrder(prev => {
      const newItems = prev.items
        .map(item => {
          if (item.product === productId) {
            const parsedQuantity = parseFloat(newQuantity);
            const validQuantity = isNaN(parsedQuantity) ? 0.01 : 
                                Math.max(0.01, parsedQuantity);
            
            return { ...item, quantity: validQuantity };
          }
          return item;
        })
        .filter(item => item.quantity > 0);
      
      return { ...prev, items: newItems };
    });
  }, []);

  // Increment/decrement quantity with decimal steps
  const adjustQuantity = useCallback((productId, change) => {
    setOrder(prev => {
      const newItems = prev.items
        .map(item => {
          if (item.product === productId) {
            const newQty = item.quantity + change;
            return newQty > 0 ? { ...item, quantity: Math.max(0.0001, newQty) } : null;
          }
          return item;
        })
        .filter(Boolean);
      
      return { ...prev, items: newItems };
    });
  }, []);

  // Remove item completely
  const removeItem = useCallback((productId) => {
    setOrder(prev => ({
      ...prev,
      items: prev.items.filter(item => item.product !== productId)
    }));
  }, []);

  // Handle payment method change
  const handlePaymentChange = useCallback((method) => {
    setPaymentMethod(method);
    setOrder(prev => ({
      ...prev,
      payment: {
        method,
        tendered: method === 'cash' ? prev.payment.tendered : total,
        changeDue: method === 'cash' ? prev.payment.tendered - total : 0
      }
    }));
  }, [total]);

  // Handle customer selection from suggestions - STORE THE ID
  const handleCustomerSelect = useCallback((customer) => {
    setCustomerQuery(customer.name);
    setOrder(prev => ({
      ...prev,
      customer: {
        id: customer._id || customer.id, // Store the customer ID
        name: customer.name,
        phone: customer.phone || '',
        accountNumber: customer.accountNumber || '',
        taxExempt: customer.taxExempt || false
      }
    }));
    setShowSuggestions(false);
  }, []);

  // Handle manual customer input
  const handleCustomerInput = useCallback((value) => {
    setCustomerQuery(value);
    setOrder(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        name: value,
        id: '' // Clear ID when manually typing
      }
    }));
  }, []);

  // Validate order before submission
  const validateOrder = useCallback(() => {
    if (order.items.length === 0) {
      return { valid: false, message: 'Please add items to the order' };
    }

    if (order.payment.method === 'cash' && order.payment.tendered < total) {
      return { 
        valid: false, 
        message: `Amount tendered (${order.payment.tendered.toFixed(2)}) is less than total (${total.toFixed(2)})` 
      };
    }

    if (order.payment.method === 'charge' && !order.customer.name.trim()) {
      return { 
        valid: false, 
        message: 'Customer name is required for charge transactions' 
      };
    }

    return { valid: true };
  }, [order, total]);

  // CORRECTED: Simple API call to backend for invoice creation
  const createInvoiceFromOrder = async (orderId, token) => {
    try {
      // Call the backend API route to create invoice
      const response = await orderAPI.createInvoice(orderId, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw new Error(`Failed to create invoice: ${error.message}`);
    }
  };

  // Submit order
  const submitOrder = async () => {
    const validation = validateOrder();
    if (!validation.valid) {
      setError({ type: 'error', message: validation.message });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = getAccessToken();
      const storeId = localStorage.getItem('storeId');
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user?.id || user?._id;

      const customerName = order.customer.name.trim() || 'Walk-in Customer';

      // Prepare order data - INCLUDE CUSTOMER ID IF AVAILABLE
      const orderData = {
        store: storeId,
        registerId: 'SJ-01',
        cashier: userId,
        cashierName: order.cashierName,
        salesman: order.salesman || null,
        customer: {
          id: order.customer.id || null, // Include customer ID if available
          name: customerName,
          phone: order.customer.phone || '',
          accountNumber: order.customer.accountNumber || '',
          taxExempt: order.customer.taxExempt || false
        },
        payment: {
          method: order.payment.method,
          tendered: order.payment.tendered || 0,
          changeDue: order.payment.changeDue || 0,
          isPaid: order.payment.method !== 'charge' // Charge sales are not immediately paid
        },
        items: order.items.map(item => ({
          product: item.product,
          quantity: item.quantity,
          priceAtSale: item.priceAtSale,
          discount: item.discount || 0,
          taxExempt: item.taxExempt || false
        })),
        subtotal: subtotal,
        tax: tax,
        discountTotal: discountTotal,
        total: total,
        projectName: order.projectName || '',
        willCall: order.willCall || false,
        deliveryRequired: order.deliveryRequired || false,
        status: 'completed'
      };

      console.log('Submitting order data:', orderData);

      // 1. Create the sales order
      const createdOrder = await orderAPI.create(orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let invoiceResult = null;
      
      // 2. If it's a charge sale, create invoice via backend API
      if (order.payment.method === 'charge') {
        console.log('Creating invoice for charge order:', createdOrder.order._id);
        invoiceResult = await createInvoiceFromOrder(createdOrder.order._id, token);
      }

      // 3. Success actions
      setOrder(initialOrderState);
      setCustomerQuery('');
      setCustomerSuggestions([]);

      // 4. Show appropriate success message
      if (order.payment.method === 'charge' && invoiceResult) {
        const invoice = invoiceResult.data?.invoice || invoiceResult.invoice;
        if (invoiceResult.accountingPosted) {
          setError({ 
            type: 'success', 
            message: `Charge Order #${createdOrder.order.orderNumber} completed! Invoice #${invoice.invoiceNumber} created and posted to accounting.` 
          });
        } else {
          setError({ 
            type: 'success', 
            message: `Charge Order #${createdOrder.order.orderNumber} completed! Invoice #${invoice.invoiceNumber} created.` 
          });
        }
      } else {
        setError({ 
          type: 'success', 
          message: `Order #${createdOrder.order.orderNumber} completed successfully!` 
        });
      }
        
    } catch (err) {
      console.error('Failed to submit order:', err);
    
      if (err.response?.status === 400) {
        setError({ 
          type: 'error', 
          message: err.response.data.error || err.response.data.message || 'Validation error' 
        });
      } else {
        setError({ 
          type: 'error', 
          message: err.message || err || 'Failed to submit order' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle tendered amount change with validation
  const handleTenderedChange = useCallback((tendered) => {
    const numericValue = parseFloat(tendered) || 0;
    setOrder(prev => ({
      ...prev,
      payment: {
        ...prev.payment,
        tendered: numericValue,
        changeDue: numericValue - total,
        isPaid: paymentMethod !== 'charge'
      }
    }));
  }, [total, paymentMethod]);

  // Continuous scan handler
  const handleContinuousScan = useCallback(async (barcode) => {
    try {
      const match = barcode.match(/^(.+)x(\d+)$/);
      const productCode = match ? match[1] : barcode;
      const quantity = match ? parseInt(match[2]) : 1;

      const product = await productAPI.getByBarcode(barcode);

      if (product) {
        addProduct(product);
        highlightProduct(product._id);
      }
    } catch (error) {
      console.error('Scan error:', error);
    }
  }, [addProduct]);

  // Toggle scanning mode
  const toggleScanning = () => {
    setScanningActive(!scanningActive);
  };

  // Format quantity for display (remove trailing zeros)
  const formatQuantity = (quantity) => {
    return parseFloat(quantity.toFixed(4)).toString();
  };

  // Check if amount tendered is sufficient
  const isAmountSufficient = order.payment.method === 'cash' 
    ? order.payment.tendered >= total
    : true;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Product Selection Panel (Left) */}
      <div className="w-3/5 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Sales Order - Retail Demo Store</h1>
          <div className="flex items-center mt-2">
            <span className="text-blue-600 font-medium">
              {format(new Date(), 'MMM d, yyyy h:mm a')}
            </span>
          </div>
        </div>

        {/* Product Search */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search by SKU or description"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Add scanning toggle button */}
        <div className="flex items-center space-x-2 mb-4">
          <BarcodeScanner 
            onScan={handleContinuousScan}
            disabled={!scanningActive}
          />
          <button
            onClick={toggleScanning}
            className={`p-2 rounded-md ${
              scanningActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}
          >
            {scanningActive ? 'Scanning Active' : 'Scanning Paused'}
          </button>
        </div>

        {scanStatus.error && (
          <div className="mb-2 p-2 bg-red-100 text-red-700 text-sm rounded">
            {scanStatus.error}
          </div>
        )}

        {/* Product Grid */}
        <div ref={productGridRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredProducts.map(product => (
            <button
              key={product._id}
              id={`product-${product._id}`}
              className="bg-white rounded-lg shadow p-3 text-left hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => addProduct(product)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-gray-900 text-wrap">{product.name}</h3>
                  <p className="text-xs text-gray-500">{product.sku}</p>
                  {product.category && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
                      {product.category.name || product.category}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-2 flex justify-between items-end">
                <span className="text-lg font-bold text-gray-900">
                  {formatNumber(product.price)}
                </span>
                <span className="text-xs text-gray-500"> {product.uom}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Order Panel (Right) */}
      <div className="w-2/5 bg-white border-l border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Current Order</h2>
        </div>

        {/* Customer and Salesman Info */}
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name {order.payment.method === 'charge' && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={order.payment.method === 'charge' ? "Enter customer name (required)" : "Enter customer name or leave blank for Walk-in"}
                value={customerQuery}
                onChange={(e) => handleCustomerInput(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
              />
              {/* Quick Add Customer Button */}
              {customerQuery.trim() && !showSuggestions && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button
                    onClick={handleQuickCreateCustomer}
                    className="text-green-600 hover:text-green-800 p-1"
                    title="Quick add customer"
                    disabled={loading}
                  >
                    <PlusCircleIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Customer Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {searchingCustomers ? (
                  <div className="px-4 py-2 text-gray-500 text-center">
                    Searching customers...
                  </div>
                ) : customerSuggestions.length > 0 ? (
                  <>
                    {customerSuggestions.map(customer => (
                      <div
                        key={customer._id || customer.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        <div className="text-sm text-gray-500">
                          {customer.accountNumber && `Account: ${customer.accountNumber} • `}
                          {customer.phone}
                          {customer.email && ` • ${customer.email}`}
                        </div>
                      </div>
                    ))}
                    {/* Option to create new customer with form */}
                    <div
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer bg-blue-50 text-blue-700 font-medium flex items-center"
                      onClick={handleOpenCustomerForm}
                    >
                      <PlusCircleIcon className="h-4 w-4 mr-2" />
                      Create new customer: "{customerQuery}"
                    </div>
                  </>
                ) : customerQuery.trim() ? (
                  <div
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer bg-blue-50 text-blue-700 font-medium flex items-center"
                    onClick={handleOpenCustomerForm}
                  >
                    <PlusCircleIcon className="h-4 w-4 mr-2" />
                    Create new customer: "{customerQuery}"
                  </div>
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-center">
                    Type to search customers
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New Customer Form Modal */}
          {showCustomerForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
                <h3 className="text-lg font-bold mb-4">Create New Customer</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newCustomerData.name}
                      onChange={(e) => setNewCustomerData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      placeholder="Enter customer name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newCustomerData.phone}
                      onChange={(e) => setNewCustomerData(prev => ({
                        ...prev,
                        phone: e.target.value
                      }))}
                      placeholder="Phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newCustomerData.email}
                      onChange={(e) => setNewCustomerData(prev => ({
                        ...prev,
                        email: e.target.value
                      }))}
                      placeholder="Email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newCustomerData.accountNumber}
                      onChange={(e) => setNewCustomerData(prev => ({
                        ...prev,
                        accountNumber: e.target.value
                      }))}
                      placeholder="Account number"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowCustomerForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitNewCustomer}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading || !newCustomerData.name.trim()}
                  >
                    {loading ? 'Creating...' : 'Create Customer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salesman</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Salesperson name (optional)"
              value={order.salesman}
              onChange={(e) => setOrder(prev => ({
                ...prev,
                salesman: e.target.value
              }))}
            />
          </div>

          {order.payment.method === 'charge' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="text-sm text-yellow-800 font-medium">
                  Charge Transaction - Customer name required
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="flex-grow overflow-y-auto p-4">
          {order.items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No items added</p>
              <p className="text-sm mt-1">Search and click products to add them</p>
            </div>
          ) : (
            <div className="space-y-3">
              {order.items.map((item) => (
                <div key={`${item.product}-${item.priceAtSale}`} className="flex justify-between items-start p-2 bg-gray-50 rounded">
                  <div className="flex-grow">
                    <div className="flex justify-between">
                      <h3 className="font-medium truncate">{item.productDetails?.name || 'Product'}</h3>
                      <span className="text-blue-600 font-medium whitespace-nowrap">
                        {(item.priceAtSale * item.quantity).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <button
                        onClick={() => adjustQuantity(item.product, -0.1)}
                        className="text-gray-500 hover:text-red-500 p-1"
                        aria-label="Decrease quantity by 0.1"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <div className="mx-2 flex items-center">
                        <input
                          type="number"
                          min="0.0001"
                          step="0.0001"
                          className="w-20 p-1 border border-gray-300 rounded text-center"
                          value={formatQuantity(item.quantity)}
                          onChange={(e) => {
                            updateQuantity(item.product, e.target.value);
                          }}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value);
                            if (isNaN(value) || value < 0.0001) {
                              updateQuantity(item.product, 0.0001);
                            }
                          }}
                        />
                        <span className="ml-1 text-xs text-gray-500">
                          {item.productDetails?.uom || 'each'}
                        </span>
                      </div>
                      <button
                        onClick={() => adjustQuantity(item.product, 0.1)}
                        className="text-gray-500 hover:text-green-500 p-1"
                        aria-label="Increase quantity by 0.1"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-1 flex justify-between text-xs">
                      <span className="text-gray-500">
                        {item.priceAtSale.toFixed(2)}/{item.productDetails?.uom || 'each'}
                      </span>
                      <span className="text-gray-500">
                        {item.quantity} × {item.priceAtSale.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.product)}
                    className="ml-2 text-gray-400 hover:text-red-500 p-1"
                    aria-label="Remove item"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Summary and Payment */}
        <div className="p-4 border-t border-gray-200">
          <div className="space-y-2 mb-4">
            <div className="flex justify-between border-t border-gray-200 pt-2">
              <span className="text-lg font-bold">Total:</span>
              <span className="text-lg font-bold">{formatNumber(total)}</span>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex space-x-2 mb-2">
              <button
                className={`flex-1 flex items-center justify-center p-2 rounded ${
                  order.payment.method === 'cash' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                }`}
                onClick={() => handlePaymentChange('cash')}
              >
                <CurrencyDollarIcon className="h-5 w-5 mr-1" />
                Cash
              </button>
              <button
                className={`flex-1 flex items-center justify-center p-2 rounded ${
                  order.payment.method === 'charge' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                }`}
                onClick={() => handlePaymentChange('charge')}
              >
                <CreditCardIcon className="h-5 w-5 mr-1" />
                Charge
              </button>
            </div>

            {order.payment.method === 'cash' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Amount Tendered {!isAmountSufficient && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`w-full p-2 border rounded ${
                    !isAmountSufficient 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  value={order.payment.tendered}
                  onChange={(e) => handleTenderedChange(e.target.value)}
                />
                {order.payment.changeDue > 0 && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Change Due:</span>
                    <span>{order.payment.changeDue.toFixed(2)}</span>
                  </div>
                )}
                {!isAmountSufficient && order.payment.tendered > 0 && (
                  <div className="flex items-center text-red-600 text-sm">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    <span>Amount tendered is less than total</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            className={`w-full py-3 rounded-md font-bold text-white ${
              order.items.length === 0 || 
              (order.payment.method === 'cash' && !isAmountSufficient) ||
              (order.payment.method === 'charge' && !order.customer.name.trim())
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            disabled={
              order.items.length === 0 || 
              (order.payment.method === 'cash' && !isAmountSufficient) ||
              (order.payment.method === 'charge' && !order.customer.name.trim()) ||
              loading
            }
            onClick={submitOrder}
          >
            {loading ? 'Processing...' : 'COMPLETE SALE'}
          </button>

          {error && (
            <div className={`mt-2 p-2 text-sm rounded ${
              error.type === 'success' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {typeof error === 'string' ? error : error.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesOrderPage;